import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { destinationHeading } from '../../lib/leadRelay/providerVocabulary';
import { storeStateLabel, summarizeLeads, summarySince, type StoreSummary } from '../../lib/leadRelay/summary';
import { getLeadSummary } from './api';
import { KC } from '../../lib/kyckConnect/links';

// The account page's Meta-lead tile (docs/lead-relay.md §10.3, §20): live leads
// this week and this month per store, with this month's failures in red.
//
// Shown on a store's own account, on every ANCESTOR (the dealer group) and on
// the store's BRAND (the marque page). An account outside the relay's reach
// renders NOTHING, not an empty card, so it is no noise on the other thousand
// account pages.
//
// ⚠️⚠️ A STORE WITH NO ROUTE SAYS SO. It does not get a row of zeroes: "0 leads"
// reads as "the ads aren't working", when nothing was ever measured. Inactive
// and test-mode routes are named too, since their zero means something
// different from a live route's.

export function LeadRelaySummaryTile({ accountId }: { accountId: string }) {
  // Fixed per mount: the week/month boundary is the viewer's local calendar.
  const now = useMemo(() => new Date(), []);
  const since = useMemo(() => summarySince(now).toISOString(), [now]);
  const { data } = useQuery({
    queryKey: ['lead-summary', accountId, since],
    queryFn: () => getLeadSummary(accountId, since),
  });
  const stores = useMemo(
    () => (data ? summarizeLeads(data.routes, data.leads, now, data.unroutedStores ?? []) : []),
    [data, now],
  );

  if (!data || stores.length === 0) return null;

  return (
    <Card data-testid="lead-relay-summary">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Meta leads relayed</CardTitle>
        <Link to={KC.relayLog} className="text-sm underline">
          Lead log
        </Link>
      </CardHeader>
      <CardContent>
        <LeadSummaryTable stores={stores} />
      </CardContent>
    </Card>
  );
}

export function LeadSummaryTable({ stores }: { stores: readonly StoreSummary[] }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-muted-foreground">
          <th className="pb-1 font-normal">{destinationHeading(stores.map((s) => s.provider))}</th>
          <th className="pb-1 text-right font-normal">This week</th>
          <th className="pb-1 text-right font-normal">This month</th>
          <th className="pb-1 text-right font-normal">Failed this month</th>
        </tr>
      </thead>
      <tbody>
        {stores.map((store) => (
          <tr key={store.key} data-state={store.state}>
            <td className="py-1">
              {store.name}
              {store.state !== 'live' && (
                <span className="block text-xs text-amber-800">{storeStateLabel(store.state, store.provider)}</span>
              )}
            </td>
            {store.state === 'not_set_up' ? (
              // Nothing was measured, so no figure is shown — not a zero.
              <td colSpan={3} className="py-1 text-right text-muted-foreground">
                Not set up
              </td>
            ) : (
              <>
                <td className="py-1 text-right tabular-nums">{store.thisWeek}</td>
                <td className="py-1 text-right tabular-nums">{store.thisMonth}</td>
                <td className="py-1 text-right tabular-nums">
                  <FailedCell store={store} />
                </td>
              </>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FailedCell({ store }: { store: StoreSummary }) {
  const failed = store.failedThisMonth ?? 0;
  const keyed = store.keyedThisMonth ?? 0;
  return (
    <>
      {failed > 0 && store.routeId ? (
        <Link
          to={`${KC.relayLog}?store=${encodeURIComponent(store.routeId)}&status=failed`}
          className="font-semibold text-red-700 underline"
        >
          {failed}
        </Link>
      ) : (
        <span className="text-muted-foreground">0</span>
      )}
      {keyed > 0 && (
        // Cleared failures are still shown, muted, so the record of them stays
        // visible — a count that simply drops would look like they never happened.
        <span className="block text-xs text-muted-foreground">{keyed} keyed in by hand</span>
      )}
    </>
  );
}
